import axios from "axios";

/**
 * Hapus seluruh cookies di browser (domain saat ini, parent domain, dan path root).
 */
export function clearAllCookies() {
  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    const hostname = window.location.hostname;
    const domainParts = hostname.split(".");

    // Daftar nama cookie yang umum dipakai di aplikasi Laravel & SPA
    const commonCookieNames = [
      "token",
      "jwt",
      "access_token",
      "access_token_peserta",
      "access_token_penilai",
      "session",
      "laravel_session",
      "XSRF-TOKEN",
      "remember_web",
    ];

    const allCookieNames = new Set(commonCookieNames);

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      if (name) allCookieNames.add(name);
    });

    const paths = ["/", window.location.pathname];
    const domains = ["", hostname, `.${hostname}`];

    if (domainParts.length > 2) {
      const rootDomain = domainParts.slice(-2).join(".");
      domains.push(rootDomain, `.${rootDomain}`);
    }

    allCookieNames.forEach((name) => {
      paths.forEach((p) => {
        domains.forEach((d) => {
          const domainAttr = d ? `; domain=${d}` : "";
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}${domainAttr}`;
          document.cookie = `${name}=; max-age=0; path=${p}${domainAttr}`;
        });
      });
    });
  } catch (e) {
    console.warn("Gagal membersihkan cookies:", e);
  }
}

/**
 * Hapus cache browser jika Cache API didukung.
 */
export async function clearAllCaches() {
  try {
    if (window.caches && window.caches.keys) {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((name) => window.caches.delete(name)));
    }
  } catch (e) {
    console.warn("Gagal membersihkan cache:", e);
  }
}

/**
 * Bersihkan seluruh data session di client (localStorage, sessionStorage, cookies, cache, dan axios headers).
 */
export function clearClientSession() {
  const keys = [
    "session_user_id",
    "session_username",
    "session_name",
    "session_path_photo",
    "session_role",
    "session_role_active_id",
    "session_role_active_name",
    "session_role_active_index",
    "session_menu_access",
    "session",
    "user",
    "configuration",
    "reload_already",
    "dateLogin",
    "roles_data_akses_menu",
    "roles_data_akses_kode_tes",
    "is_validator",
    "is_penyusunSoal",
    "isWrite",
    "isRead",
    "menu_read_names",
    "menu_write_names",
    "kode_tes_active",
    "user_pegawai",
    "filtered_menu",
    "filtered_name_menu",

    // legacy cleanup
    "access_token",
    "access_token_penilai",
    "access_token_peserta",
  ];

  try {
    keys.forEach((key) => localStorage.removeItem(key));
    localStorage.clear();
  } catch (e) {
    console.warn("Gagal membersihkan localStorage:", e);
  }

  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn("Gagal membersihkan sessionStorage:", e);
  }

  try {
    if (axios && axios.defaults && axios.defaults.headers) {
      if (axios.defaults.headers.common) {
        delete axios.defaults.headers.common["Authorization"];
        delete axios.defaults.headers.common["X-XSRF-TOKEN"];
      }
    }
  } catch (e) {
    // ignore
  }

  clearAllCookies();
  clearAllCaches();
}

/**
 * Decode payload JWT tanpa external library secara aman
 */
export function parseJwt(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    try {
      const parts = token.split(".");
      if (parts.length >= 2) {
        return JSON.parse(atob(parts[1]));
      }
    } catch (e2) {
      // ignore
    }
    return null;
  }
}

/**
 * Ambil data user pegawai yang tersimpan di localStorage dengan aman (mendukung berbagai format session).
 */
export function getStoredUser() {
  try {
    // Fallback: sessionStorage jika localStorage dikosongkan
    const sessionUser = sessionStorage.getItem("user");
    if (sessionUser && sessionUser !== "undefined" && sessionUser !== "null") {
      try {
        const parsed = JSON.parse(sessionUser);
        if (parsed && typeof parsed === "object") {
          localStorage.setItem("user", sessionUser);
          return parsed;
        }
      } catch (e) {
        // ignore
      }
    }

    const rawUser = localStorage.getItem("user");
    if (
      rawUser &&
      rawUser !== "[object Object]" &&
      rawUser !== "undefined" &&
      rawUser !== "null"
    ) {
      try {
        const parsed = JSON.parse(rawUser);
        if (parsed && typeof parsed === "object") return parsed;
      } catch (e) {
        // bukan JSON string
      }
    }

    const rawSession =
      localStorage.getItem("session") || sessionStorage.getItem("session");
    if (rawSession && rawSession !== "undefined" && rawSession !== "null") {
      try {
        const parsedSession = JSON.parse(rawSession);
        if (parsedSession?.user) return parsedSession.user;
        if (parsedSession?.pegawai) return parsedSession.pegawai;
        if (parsedSession?.data?.user) return parsedSession.data.user;
      } catch (e) {
        // ignore
      }
    }

    // Fallback: ekstrak dari JWT access_token bila localStorage user/session belum tersimpan
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token") ||
      localStorage.getItem("access_token_peserta");
    if (token) {
      const jwt = parseJwt(token);
      if (jwt) {
        const idFromJwt = jwt.sub || jwt.pegawai?.id || jwt.users?.id;
        const nipFromJwt =
          jwt.pegawai?.nip ||
          jwt.users?.username ||
          localStorage.getItem("session_username") ||
          sessionStorage.getItem("session_username");
        const nameFromJwt =
          jwt.pegawai?.name ||
          jwt.users?.name ||
          localStorage.getItem("session_name") ||
          sessionStorage.getItem("session_name");

        if (idFromJwt || nipFromJwt) {
          const fallbackUser = {
            idpegawai_internal: idFromJwt,
            id: idFromJwt,
            u_nip: nipFromJwt,
            nama: nameFromJwt,
          };
          try {
            localStorage.setItem("user", JSON.stringify(fallbackUser));
          } catch (e) {
            // ignore
          }
          return fallbackUser;
        }
      }
    }
  } catch (e) {
    console.warn("Gagal membaca stored user dari localStorage:", e);
  }
  return null;
}

/**
 * Pulihkan session pegawai internal bila terjadi sync issue antara
 * localStorage dan sessionStorage atau setelah navigation.
 */
export function restorePegawaiSession() {
  try {
    const keys = [
      "session_user_id",
      "session_username",
      "session_name",
      "access_token",
      "session",
      "user",
      "user_pegawai",
      "configuration",
      "filtered_menu",
    ];

    const localToken = localStorage.getItem("access_token");
    const sessionToken = sessionStorage.getItem("access_token");

    // Sinkronkan dari sessionStorage ke localStorage jika localStorage kosong
    if (!localToken && sessionToken) {
      keys.forEach((key) => {
        const val = sessionStorage.getItem(key);
        if (val) localStorage.setItem(key, val);
      });
    } else if (localToken && !sessionToken) {
      keys.forEach((key) => {
        const val = localStorage.getItem(key);
        if (val) sessionStorage.setItem(key, val);
      });
    }

    // Ekstrak dari token aktif jika session_user_id belum ada
    const activeToken =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    if (activeToken) {
      const jwt = parseJwt(activeToken);
      if (jwt) {
        const id = jwt.sub || jwt.pegawai?.id || jwt.users?.id;
        const nip = jwt.pegawai?.nip || jwt.users?.username;
        const name = jwt.pegawai?.name || jwt.users?.name;

        if (id) {
          if (!localStorage.getItem("session_user_id")) {
            localStorage.setItem("session_user_id", String(id));
          }
          if (!sessionStorage.getItem("session_user_id")) {
            sessionStorage.setItem("session_user_id", String(id));
          }
        }
        if (nip) {
          if (!localStorage.getItem("session_username")) {
            localStorage.setItem("session_username", String(nip));
          }
          if (!sessionStorage.getItem("session_username")) {
            sessionStorage.setItem("session_username", String(nip));
          }
        }
        if (name) {
          if (!localStorage.getItem("session_name")) {
            localStorage.setItem("session_name", String(name));
          }
          if (!sessionStorage.getItem("session_name")) {
            sessionStorage.setItem("session_name", String(name));
          }
        }
        if (!localStorage.getItem("user_pegawai")) {
          localStorage.setItem("user_pegawai", "pegawai_internal");
          sessionStorage.setItem("user_pegawai", "pegawai_internal");
        }
      }
    }
  } catch (e) {
    console.warn("restorePegawaiSession warning:", e);
  }
}

/**
 * Ambil ID user / idpegawai_internal yang valid dari localStorage.
 * Mengembalikan null jika id bernilai null, "null", "undefined", 0, atau kosong.
 */
export function getStoredUserId() {
  restorePegawaiSession();

  const directId =
    localStorage.getItem("session_user_id") ||
    sessionStorage.getItem("session_user_id");
  if (
    directId &&
    directId !== "null" &&
    directId !== "undefined" &&
    directId.trim() !== ""
  ) {
    const validId = directId.trim();
    if (!localStorage.getItem("session_user_id")) {
      localStorage.setItem("session_user_id", validId);
    }
    return validId;
  }

  const user = getStoredUser();
  const fallbackId = user?.idpegawai_internal ?? user?.id ?? null;
  if (
    fallbackId &&
    String(fallbackId) !== "null" &&
    String(fallbackId) !== "undefined"
  ) {
    try {
      localStorage.setItem("session_user_id", String(fallbackId));
      sessionStorage.setItem("session_user_id", String(fallbackId));
      if (user?.u_nip && !localStorage.getItem("session_username")) {
        localStorage.setItem("session_username", user.u_nip);
      }
      if (user?.nama && !localStorage.getItem("session_name")) {
        localStorage.setItem("session_name", user.nama);
      }
    } catch (e) {
      // ignore
    }
    return String(fallbackId);
  }

  // Fallback terakhir: cek langsung dari payload JWT
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token_peserta");
  if (token) {
    const jwt = parseJwt(token);
    const jwtId = jwt?.sub || jwt?.pegawai?.id || jwt?.users?.id;
    if (jwtId && String(jwtId) !== "null" && String(jwtId) !== "undefined") {
      try {
        localStorage.setItem("session_user_id", String(jwtId));
        sessionStorage.setItem("session_user_id", String(jwtId));
        if (jwt?.pegawai?.nip || jwt?.users?.username) {
          localStorage.setItem(
            "session_username",
            jwt.pegawai?.nip || jwt.users?.username,
          );
        }
        if (jwt?.pegawai?.name || jwt?.users?.name) {
          localStorage.setItem(
            "session_name",
            jwt.pegawai?.name || jwt.users?.name,
          );
        }
      } catch (e) {
        // ignore
      }
      return String(jwtId);
    }
  }

  return null;
}


